import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Trash2 } from "lucide-react";
import type { Contact } from "@/types";
import { useContacts, useSaveContact } from "../api";
import { EmptyState } from "@/components/shared/EmptyState";
import { DeleteDialog } from "@/components/shared/DeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const schema = z.object({
  first_name: z.string().trim().min(1, "Prénom requis").max(80),
  last_name: z.string().trim().max(80).optional().or(z.literal("")),
  email: z.string().trim().email("Email invalide").optional().or(z.literal("")),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  job_title: z.string().trim().max(80).optional().or(z.literal("")),
  is_primary: z.boolean(), is_billing: z.boolean(), is_delivery: z.boolean(), is_signatory: z.boolean(),
});
type FormValues = z.infer<typeof schema>;
const nz = (s?: string) => (s && s.trim() ? s.trim() : null);

function ContactForm({ clientId, contact, onDone }: { clientId: string; contact?: Contact; onDone: () => void }) {
  const save = useSaveContact(clientId);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: contact?.first_name ?? "", last_name: contact?.last_name ?? "", email: contact?.email ?? "",
      phone: contact?.phone ?? "", job_title: contact?.job_title ?? "",
      is_primary: contact?.is_primary ?? false, is_billing: contact?.is_billing ?? false,
      is_delivery: contact?.is_delivery ?? false, is_signatory: contact?.is_signatory ?? false,
    },
  });
  const err = form.formState.errors;
  const flags: { k: "is_primary" | "is_billing" | "is_delivery" | "is_signatory"; label: string }[] = [
    { k: "is_primary", label: "Interlocuteur principal" }, { k: "is_billing", label: "Reçoit les factures" },
    { k: "is_delivery", label: "Reçoit les leads" }, { k: "is_signatory", label: "Signataire des contrats" },
  ];
  return (
    <form
      onSubmit={form.handleSubmit((v) => save.mutate(
        { id: contact?.id, values: { first_name: v.first_name, last_name: nz(v.last_name), email: nz(v.email), phone: nz(v.phone), job_title: nz(v.job_title), is_primary: v.is_primary, is_billing: v.is_billing, is_delivery: v.is_delivery, is_signatory: v.is_signatory } },
        { onSuccess: onDone },
      ))}
      className="space-y-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label htmlFor="first_name">Prénom *</Label><Input id="first_name" {...form.register("first_name")} />{err.first_name && <p className="text-xs text-destructive">{err.first_name.message}</p>}</div>
        <div className="space-y-1.5"><Label htmlFor="last_name">Nom</Label><Input id="last_name" {...form.register("last_name")} /></div>
        <div className="space-y-1.5"><Label htmlFor="email">Email</Label><Input id="email" type="email" {...form.register("email")} />{err.email && <p className="text-xs text-destructive">{err.email.message}</p>}</div>
        <div className="space-y-1.5"><Label htmlFor="phone">Téléphone</Label><Input id="phone" {...form.register("phone")} /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="job_title">Fonction</Label><Input id="job_title" {...form.register("job_title")} /></div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {flags.map(({ k, label }) => (
          <label key={k} className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.watch(k)} onCheckedChange={(c) => form.setValue(k, c === true)} /> {label}
          </label>
        ))}
      </div>
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>{contact ? "Enregistrer" : "Ajouter"}</Button></div>
    </form>
  );
}

export function ContactsTab({ clientId }: { clientId: string }) {
  const { data: contacts, isLoading } = useContacts(clientId);
  const [editing, setEditing] = useState<Contact | "new" | null>(null);
  const [deleting, setDeleting] = useState<Contact | null>(null);

  return (
    <div>
      <div className="mb-4 flex justify-end"><Button size="sm" onClick={() => setEditing("new")}><Plus className="mr-2 h-4 w-4" />Ajouter un interlocuteur</Button></div>
      {isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !contacts?.length ? (
        <EmptyState title="Aucun interlocuteur" description="Ajoute les personnes à contacter chez ce client : commercial, facturation, réception des leads, signataire." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {contacts.map((c) => (
            <div key={c.id} className="glass rounded-2xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium">{c.first_name} {c.last_name}</div>
                  {c.job_title && <div className="text-xs text-muted-foreground">{c.job_title}</div>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>Modifier</Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleting(c)} aria-label="Supprimer cet interlocuteur"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-2 space-y-0.5 text-sm">
                {c.email && <div><a className="hover:underline" href={`mailto:${c.email}`}>{c.email}</a></div>}
                {c.phone && <div><a className="hover:underline" href={`tel:${c.phone}`}>{c.phone}</a></div>}
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {c.is_primary && <Badge>Principal</Badge>}
                {c.is_billing && <Badge variant="secondary">Facturation</Badge>}
                {c.is_delivery && <Badge variant="secondary">Livraison</Badge>}
                {c.is_signatory && <Badge variant="secondary">Signataire</Badge>}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nouvel interlocuteur" : "Modifier l'interlocuteur"}</DialogTitle></DialogHeader>
          {editing !== null && <ContactForm clientId={clientId} contact={editing === "new" ? undefined : editing} onDone={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
      {deleting && <DeleteDialog entity="contact" id={deleting.id} open onOpenChange={(o) => !o && setDeleting(null)} onDeleted={() => setDeleting(null)} />}
    </div>
  );
}
